import { addDays, dayOfWeek, isDateKey, isMonthKey, mondayOf, monthOfWeek, prevMonth, round1, weeksOfMonth, type DateKey, type MonthKey } from './dates.js';

/** DATA-1028 `core.availability_submission.unit` */
export type AvailabilityUnit = 'percent' | 'hours';

export const PERCENT_MAX = 100;
export const HOURS_MAX = 60;
export const WEEKEND_DAY_MAX = 8;
export const DEFAULT_WEEKLY_BASE_H = 40;

/** One submission: a value per week of the month (Mon–Fri) plus optional weekend hours per day. */
export interface AvailabilitySubmission {
  personKey: string;
  month: MonthKey;
  unit: AvailabilityUnit;
  /** Week Monday → value (percent 0–100 or hours 0–60). */
  weeks: Record<DateKey, number>;
  /** Saturday/Sunday → hours (0 < h ≤ 8); only days listed are usable for weekend work. */
  weekend: Record<DateKey, number>;
  submittedAt?: string;
}

export interface AvailabilityDraft {
  month: MonthKey;
  unit: AvailabilityUnit;
  weeks: Record<string, number | string | null | undefined>;
  weekend?: Record<string, number | string | null | undefined>;
}

export type AvailabilityErrorCode =
  | 'bad_month'
  | 'bad_unit'
  | 'week_missing'
  | 'percent_range'
  | 'hours_range'
  | 'unknown_week'
  | 'weekend_range'
  | 'weekend_bad_day';

export interface AvailabilityError {
  code: AvailabilityErrorCode;
  week?: DateKey;
  day?: string;
}

/** Field messages – English (staff, prototype wording) and Swedish du-form (freelancer pages). */
export const AVAILABILITY_MESSAGES: Record<'en' | 'sv', Record<AvailabilityErrorCode, string>> = {
  en: {
    bad_month: 'Pick a month.',
    bad_unit: 'Choose percent or hours.',
    week_missing: "Fill in every week – 0 is fine if you can't work that week.",
    percent_range: 'Percent is 0–100 for each week.',
    hours_range: 'Hours are 0–60 for each week.',
    unknown_week: 'That week is not part of the month.',
    weekend_range: 'Weekend hours are 0–8 per day (a day is at most 8 h).',
    weekend_bad_day: 'Weekend hours can only be entered for a Saturday or Sunday in the month’s weeks.'
  },
  sv: {
    bad_month: 'Välj en månad.',
    bad_unit: 'Välj procent eller timmar.',
    week_missing: 'Fyll i varje vecka – 0 går bra om du inte kan jobba den veckan.',
    percent_range: 'Procent ska vara 0–100 för varje vecka.',
    hours_range: 'Timmar ska vara 0–60 för varje vecka.',
    unknown_week: 'Den veckan hör inte till månaden.',
    weekend_range: 'Helgtimmar är 0–8 per dag (en dag är högst 8 h).',
    weekend_bad_day: 'Helgtimmar kan bara läggas in för en lördag eller söndag i månadens veckor.'
  }
};

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** The weekend days (Sat, Sun) that belong to a month's weeks. */
export function weekendDaysOfMonth(m: MonthKey): DateKey[] {
  return weeksOfMonth(m).flatMap((w) => [addDays(w, 5), addDays(w, 6)]);
}

/**
 * Validate and normalise a submission (UX-1060, UX-1061, DATA-1028): every week of the month is required, 0 allowed,
 * percent 0–100, hours 0–60; weekend days 0–8 h, empty or 0 = not available (not stored).
 */
export function validateAvailability(
  personKey: string,
  draft: AvailabilityDraft
): { ok: true; value: AvailabilitySubmission } | { ok: false; errors: AvailabilityError[] } {
  const errors: AvailabilityError[] = [];
  if (!isMonthKey(draft.month)) return { ok: false, errors: [{ code: 'bad_month' }] };
  if (draft.unit !== 'percent' && draft.unit !== 'hours') return { ok: false, errors: [{ code: 'bad_unit' }] };
  const weeks = weeksOfMonth(draft.month);
  const max = draft.unit === 'percent' ? PERCENT_MAX : HOURS_MAX;
  const out: Record<DateKey, number> = {};
  for (const w of weeks) {
    const v = num(draft.weeks?.[w]);
    if (v === null) errors.push({ code: 'week_missing', week: w });
    else if (v < 0 || v > max) errors.push({ code: draft.unit === 'percent' ? 'percent_range' : 'hours_range', week: w });
    else out[w] = v;
  }
  for (const k of Object.keys(draft.weeks ?? {})) if (!weeks.includes(k)) errors.push({ code: 'unknown_week', week: k });
  const allowedWeekend = new Set(weekendDaysOfMonth(draft.month));
  const weekend: Record<DateKey, number> = {};
  for (const [day, raw] of Object.entries(draft.weekend ?? {})) {
    const v = num(raw);
    if (v === null || v === 0) continue;
    if (!isDateKey(day) || !allowedWeekend.has(day)) {
      errors.push({ code: 'weekend_bad_day', day });
      continue;
    }
    if (v < 0 || v > WEEKEND_DAY_MAX) errors.push({ code: 'weekend_range', day });
    else weekend[day] = v;
  }
  if (errors.length) return { ok: false, errors };
  return { ok: true, value: { personKey, month: draft.month, unit: draft.unit, weeks: out, weekend } };
}

/** Hours for a week value (percent → hours with the person's weekly base). */
export const hoursOfValue = (unit: AvailabilityUnit, value: number, weeklyBaseH: number = DEFAULT_WEEKLY_BASE_H): number =>
  unit === 'percent' ? round1((value / 100) * weeklyBaseH) : value;

/**
 * *Same as last month*: last month's unit and values week by week; when the new month has more weeks the last week of the
 * previous month is reused. Weekend hours are copied by position (Sat/Sun of the n-th week). Only fills the form.
 */
export function sameAsLastMonth(prev: AvailabilitySubmission, month: MonthKey): AvailabilityDraft {
  const lw = weeksOfMonth(prevMonth(month)).filter((w) => prev.weeks[w] !== undefined);
  const prevWeeks = lw.length ? lw : Object.keys(prev.weeks).sort();
  const weeks: Record<string, number> = {};
  const weekend: Record<string, number> = {};
  weeksOfMonth(month).forEach((w, i) => {
    const src = prevWeeks[Math.min(i, prevWeeks.length - 1)];
    if (src === undefined) return;
    const v = prev.weeks[src];
    if (v !== undefined) weeks[w] = v;
    for (const off of [5, 6]) {
      const h = prev.weekend[addDays(src, off)];
      if (h) weekend[addDays(w, off)] = h;
    }
  });
  return { month, unit: prev.unit, weeks, weekend };
}

/**
 * A freelancer's capacity for one week: the submission of the week's month (Thursday rule) – the week value (Mon–Fri) plus
 * the weekend hours of that week. null = no availability submitted.
 */
export function freelancerWeekCapacity(sub: AvailabilitySubmission | undefined, weekMonday: DateKey, weeklyBaseH: number = DEFAULT_WEEKLY_BASE_H): number | null {
  const w = mondayOf(weekMonday);
  if (!sub || sub.month !== monthOfWeek(w)) return null;
  const v = sub.weeks[w];
  if (v === undefined) return null;
  const wkend = (sub.weekend[addDays(w, 5)] ?? 0) + (sub.weekend[addDays(w, 6)] ?? 0);
  return round1(hoursOfValue(sub.unit, v, weeklyBaseH) + wkend);
}

/** Weekend day → hours from submissions (input to placement: weekend work only on these days). */
export function weekendHoursFrom(subs: AvailabilitySubmission[]): Record<DateKey, number> {
  const out: Record<DateKey, number> = {};
  for (const s of subs) for (const [d, h] of Object.entries(s.weekend)) if (dayOfWeek(d) === 0 || dayOfWeek(d) === 6) out[d] = Math.min(WEEKEND_DAY_MAX, h);
  return out;
}

/** "Please send it by Sun 25 Oct" – the 25th of the month before. */
export const dueDayOf = (m: MonthKey): DateKey => `${prevMonth(m)}-25`;
